import { FormEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import { registerSchema } from '@fellowship/shared'
import { useRegister } from '../hooks/useAuth'
import { ApiError } from '../lib/api'
import { Wordmark } from '../components/ui/Wordmark'
import { SocialAuthButtons } from '../components/auth/SocialAuthButtons'

export default function RegisterPage() {
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; displayName?: string; password?: string }>({})
  const register = useRegister()

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFieldErrors({})
    const parsed = registerSchema.safeParse({ email, password, displayName })
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors
      setFieldErrors({ email: flat.email?.[0], displayName: flat.displayName?.[0], password: flat.password?.[0] })
      return
    }
    register.mutate(parsed.data)
  }

  const serverError =
    register.error instanceof ApiError
      ? register.error.code === 'EMAIL_IN_USE'
        ? 'An account with this email already exists.'
        : register.error.message
      : null

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', fontFamily: '"Outfit", system-ui, sans-serif' }}>
      <div style={{ marginBottom: 28 }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Wordmark size={24} />
        </Link>
      </div>

      {/* Register card */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 24,
        padding: '40px 44px',
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 1px 2px rgba(31,26,46,.04), 0 18px 40px rgba(31,26,46,.07)',
      }}>
        {/* Eyebrow */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 14 }}>
          NEW HERE
        </div>

        <h1 style={{ margin: '0 0 24px', fontSize: 32, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--ink)', lineHeight: 1.1 }}>
          Create your account
        </h1>

        <SocialAuthButtons />

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
          <div style={{ flex: 1, height: 1, background: 'var(--line-soft)' }} />
          <span style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 500 }}>or sign up with email</span>
          <div style={{ flex: 1, height: 1, background: 'var(--line-soft)' }} />
        </div>

        {serverError && (
          <div role="alert" style={{ padding: '12px 16px', borderRadius: 12, marginBottom: 20, background: '#fee2e2', border: '1px solid #fca5a5', color: '#b91c1c', fontSize: 13.5 }}>
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="displayName" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
              Name
            </label>
            <input
              id="displayName"
              type="text"
              autoComplete="name"
              placeholder="Your full name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input"
              aria-describedby={fieldErrors.displayName ? 'name-error' : undefined}
              aria-invalid={Boolean(fieldErrors.displayName)}
            />
            {fieldErrors.displayName && (
              <div id="name-error" style={{ fontSize: 12, color: '#b91c1c' }}>{fieldErrors.displayName}</div>
            )}
          </div>

          <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="email" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              aria-describedby={fieldErrors.email ? 'email-error' : undefined}
              aria-invalid={Boolean(fieldErrors.email)}
            />
            {fieldErrors.email && (
              <div id="email-error" style={{ fontSize: 12, color: '#b91c1c' }}>{fieldErrors.email}</div>
            )}
          </div>

          <div style={{ marginBottom: 28, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="password" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>
              Password
              <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6 }}>8+ characters</span>
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              aria-describedby={fieldErrors.password ? 'password-error' : undefined}
              aria-invalid={Boolean(fieldErrors.password)}
            />
            {fieldErrors.password && (
              <div id="password-error" style={{ fontSize: 12, color: '#b91c1c' }}>{fieldErrors.password}</div>
            )}
          </div>

          <button
            type="submit"
            disabled={register.isPending}
            className="btn btn--full btn--lg"
          >
            {register.isPending ? 'Creating account…' : 'Create account'}
          </button>

          <p style={{ marginTop: 14, textAlign: 'center', fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
            By creating an account you agree to our{' '}
            <Link to="/terms" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Terms</Link>{' '}
            and{' '}
            <Link to="/privacy" style={{ color: 'var(--ink-2)', textDecoration: 'none' }}>Privacy Policy</Link>.
          </p>
        </form>

        <p style={{ marginTop: 20, textAlign: 'center', fontSize: 13.5, color: 'var(--muted)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>
            Log in
          </Link>
        </p>
      </div>
    </div>
  )
}
